/**
 * SellerSubscriptionsPage - Subscription tiers and listing boosts
 * Pro/Business tiers with lower commission, Featured listing purchases
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Crown, Star, Zap, Check, Rocket, TrendingUp, 
  Shield, Headphones, BarChart3, Upload, Clock,
  CreditCard, Sparkles, Gift
} from 'lucide-react';
import { toast } from 'sonner';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tier Card Component
const TierCard = ({ tier, currentTier, currencySymbol, onSelect, isPopular }) => {
  const isCurrentPlan = currentTier === tier.id;
  const isUpgrade = tier.id !== 'free' && currentTier === 'free';
  
  const getTierIcon = (tierId) => {
    switch (tierId) {
      case 'pro': return <Star className="w-8 h-8 text-purple-500" />;
      case 'business': return <Crown className="w-8 h-8 text-amber-500" />;
      default: return <Zap className="w-8 h-8 text-gray-500" />;
    }
  };
  
  const getTierGradient = (tierId) => {
    switch (tierId) {
      case 'pro': return 'from-purple-500 to-pink-500';
      case 'business': return 'from-amber-500 to-orange-500';
      default: return 'from-gray-400 to-gray-500';
    }
  };
  
  return (
    <Card 
      className={`relative ${isCurrentPlan ? 'border-2 border-blue-500' : ''} ${isPopular ? 'scale-105 shadow-xl' : ''}`}
      data-testid={`tier-card-${tier.id}`}
    >
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-gradient-to-r from-purple-500 to-pink-500">
            Most Popular
          </Badge>
        </div>
      )}
      
      {isCurrentPlan && (
        <div className="absolute -top-3 right-4">
          <Badge className="bg-blue-500">Current Plan</Badge>
        </div>
      )}
      
      <CardHeader className="text-center pb-2">
        <div className="mx-auto mb-2">
          {getTierIcon(tier.id)}
        </div>
        <CardTitle className="text-2xl">{tier.name}</CardTitle>
        <div className="mt-2">
          <span className="text-4xl font-bold">{currencySymbol}{tier.price}</span>
          {tier.price > 0 && <span className="text-gray-500">/month</span>}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className={`p-3 rounded-lg bg-gradient-to-r ${getTierGradient(tier.id)} text-white text-center`}>
          <p className="text-sm opacity-80">Commission Rate</p>
          <p className="text-2xl font-bold">{tier.commission_pct}%</p>
        </div>
        
        <ul className="space-y-2">
          {tier.features.map((feature, i) => (
            <li key={i} className="flex items-start gap-2 text-sm">
              <Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
              <span>{feature}</span>
            </li>
          ))}
          
          {tier.max_listings_per_month > 0 && (
            <li className="flex items-start gap-2 text-sm">
              <Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
              <span>{tier.max_listings_per_month} listings per month</span>
            </li>
          )}
          {tier.max_listings_per_month === -1 && (
            <li className="flex items-start gap-2 text-sm">
              <Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
              <span>Unlimited listings</span>
            </li>
          )}
          
          {tier.featured_listings > 0 && (
            <li className="flex items-start gap-2 text-sm">
              <Sparkles className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
              <span>{tier.featured_listings} free featured listings/month</span>
            </li>
          )}
        </ul>
        
        <Button 
          className="w-full" 
          variant={isCurrentPlan ? "outline" : "default"}
          disabled={isCurrentPlan}
          onClick={() => onSelect(tier)}
        >
          {isCurrentPlan ? 'Current Plan' : isUpgrade ? 'Upgrade' : 'Select Plan'}
        </Button>
      </CardContent>
    </Card>
  );
};

// Boost Option Card
const BoostCard = ({ boost, currencySymbol, onPurchase }) => (
  <Card className="hover:shadow-lg transition-all" data-testid={`boost-card-${boost.id}`}>
    <CardContent className="p-6 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 flex items-center justify-center">
        <Rocket className="w-8 h-8 text-white" />
      </div>
      
      <h3 className="text-xl font-bold">{boost.name}</h3>
      <p className="text-gray-500 mb-4">{boost.duration_days} days of visibility</p>
      
      <div className="text-3xl font-bold text-orange-600 mb-4">
        {currencySymbol}{boost.price}
      </div>
      
      <ul className="text-sm text-gray-600 space-y-2 mb-4">
        <li className="flex items-center justify-center gap-2">
          <TrendingUp className="w-4 h-4 text-green-500" />
          Top of search results
        </li>
        <li className="flex items-center justify-center gap-2">
          <Star className="w-4 h-4 text-amber-500" />
          Featured badge
        </li>
        <li className="flex items-center justify-center gap-2">
          <Clock className="w-4 h-4 text-blue-500" />
          {boost.duration_days} days duration
        </li>
      </ul>
      
      <Button className="w-full" onClick={() => onPurchase(boost)}>
        <CreditCard className="w-4 h-4 mr-2" />
        Purchase Boost
      </Button>
    </CardContent>
  </Card>
);

export default function SellerSubscriptionsPage() {
  const navigate = useNavigate();
  const { country } = useCountry();
  
  const [tiers, setTiers] = useState([]);
  const [boosts, setBoosts] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showBoostModal, setShowBoostModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedBoost, setSelectedBoost] = useState(null);
  const [email, setEmail] = useState('');
  const [listingId, setListingId] = useState('');
  
  const currencySymbol = country === 'UK' ? '£' : 'Rs.';
  
  // Fetch data
  useEffect(() => {
    fetchData();
  }, [country]);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch subscription tiers
      const tiersRes = await fetch(`${API_URL}/api/books-media/subscriptions/tiers?country=${country}`);
      const tiersData = await tiersRes.json();
      setTiers(tiersData.tiers || []);
      
      // Fetch boost options
      const boostsRes = await fetch(`${API_URL}/api/books-media/boosts/options?country=${country}`);
      const boostsData = await boostsRes.json();
      setBoosts(boostsData.options || []);
      
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSelectTier = (tier) => {
    setSelectedTier(tier);
    setShowUpgradeModal(true);
  };
  
  const handlePurchaseBoost = (boost) => {
    setSelectedBoost(boost);
    setShowBoostModal(true);
  };
  
  const handleSubscribe = async () => {
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/books-media/subscriptions/create?country=${country}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          seller_email: email,
          seller_name: email.split('@')[0],
          tier: selectedTier.id
        })
      });
      
      const data = await response.json();
      
      if (data.requires_payment) {
        toast.info('Redirecting to payment...');
        // In production, integrate with Stripe checkout
      } else {
        toast.success('Subscription activated!');
      }
      
      setShowUpgradeModal(false);
      setCurrentSubscription(selectedTier);
    } catch (err) {
      toast.error('Failed to create subscription');
    }
  };
  
  const handleBuyBoost = async () => {
    if (!email || !listingId) {
      toast.error('Please enter your email and listing ID');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/books-media/boosts/purchase?country=${country}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          listing_id: listingId,
          boost_type: selectedBoost.id,
          buyer_email: email
        })
      });
      
      const data = await response.json();
      
      if (data.requires_payment) {
        toast.info('Boost created! Complete payment to activate.');
        // In production, integrate with Stripe checkout
      }
      
      setShowBoostModal(false);
    } catch (err) {
      toast.error('Failed to purchase boost');
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6">
            <Crown className="w-5 h-5" />
            <span className="font-medium">Seller Programs</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Sell More, Earn More
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            Choose a subscription tier for lower commissions, or boost individual listings for maximum visibility.
          </p>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-12">
        <Tabs defaultValue="subscriptions">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8">
            <TabsTrigger value="subscriptions">
              <Crown className="w-4 h-4 mr-2" />
              Subscriptions
            </TabsTrigger>
            <TabsTrigger value="boosts">
              <Rocket className="w-4 h-4 mr-2" />
              Listing Boosts
            </TabsTrigger>
          </TabsList>
          
          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold">Choose Your Plan</h2>
              <p className="text-gray-600">Lower commissions mean more money in your pocket</p>
            </div>
            
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                {[...Array(3)].map((_, i) => (
                  <Card key={i}>
                    <CardContent className="p-6 space-y-4">
                      <div className="h-8 bg-gray-200 rounded animate-pulse" />
                      <div className="h-12 bg-gray-200 rounded animate-pulse" />
                      <div className="h-40 bg-gray-200 rounded animate-pulse" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto items-start">
                {tiers.map((tier, index) => (
                  <TierCard
                    key={tier.id}
                    tier={tier}
                    currentTier={currentSubscription?.id || 'free'}
                    currencySymbol={currencySymbol}
                    onSelect={handleSelectTier}
                    isPopular={index === 1}
                  />
                ))}
              </div>
            )}
            
            {/* Benefits Section */}
            <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card>
                <CardContent className="p-6 text-center">
                  <Shield className="w-10 h-10 mx-auto mb-3 text-blue-500" />
                  <h4 className="font-semibold">Secure Payments</h4>
                  <p className="text-sm text-gray-500">All transactions protected</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <Headphones className="w-10 h-10 mx-auto mb-3 text-green-500" />
                  <h4 className="font-semibold">Priority Support</h4>
                  <p className="text-sm text-gray-500">Fast response times</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <BarChart3 className="w-10 h-10 mx-auto mb-3 text-purple-500" />
                  <h4 className="font-semibold">Analytics</h4>
                  <p className="text-sm text-gray-500">Track your performance</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <Upload className="w-10 h-10 mx-auto mb-3 text-orange-500" />
                  <h4 className="font-semibold">Bulk Upload</h4>
                  <p className="text-sm text-gray-500">List items faster</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Boosts Tab */}
          <TabsContent value="boosts">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold">Boost Your Listings</h2>
              <p className="text-gray-600">Get more visibility and sell faster</p>
            </div>
            
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                {[...Array(3)].map((_, i) => (
                  <Card key={i}>
                    <CardContent className="p-6 space-y-4">
                      <div className="h-16 w-16 mx-auto bg-gray-200 rounded-full animate-pulse" />
                      <div className="h-6 bg-gray-200 rounded animate-pulse" />
                      <div className="h-10 bg-gray-200 rounded animate-pulse" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                {boosts.map(boost => (
                  <BoostCard
                    key={boost.id}
                    boost={boost}
                    currencySymbol={currencySymbol}
                    onPurchase={handlePurchaseBoost}
                  />
                ))}
              </div>
            )}
            
            {/* How It Works */}
            <div className="mt-12 max-w-3xl mx-auto">
              <h3 className="text-xl font-bold text-center mb-6">How Boosts Work</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-3">
                    <span className="text-xl font-bold text-orange-600">1</span>
                  </div>
                  <h4 className="font-medium">Select a Boost</h4>
                  <p className="text-sm text-gray-500">Choose duration that fits your needs</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-3">
                    <span className="text-xl font-bold text-orange-600">2</span>
                  </div>
                  <h4 className="font-medium">Complete Payment</h4>
                  <p className="text-sm text-gray-500">Secure checkout via Stripe</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-3">
                    <span className="text-xl font-bold text-orange-600">3</span>
                  </div>
                  <h4 className="font-medium">Get Featured</h4>
                  <p className="text-sm text-gray-500">Your listing appears at the top!</p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Upgrade Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upgrade to {selectedTier?.name}</DialogTitle>
            <DialogDescription>
              Enjoy {selectedTier?.commission_pct}% commission and exclusive benefits
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex justify-between items-center">
                <span>Monthly Price:</span>
                <span className="text-2xl font-bold">{currencySymbol}{selectedTier?.price}</span>
              </div>
            </div>
            
            <div>
              <Label>Your Email</Label>
              <Input
                type="email"
                placeholder="seller@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            
            <Alert>
              <Gift className="w-4 h-4" />
              <AlertDescription>
                First month 50% off for new subscribers!
              </AlertDescription>
            </Alert>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpgradeModal(false)}>Cancel</Button>
            <Button onClick={handleSubscribe}>
              <CreditCard className="w-4 h-4 mr-2" />
              Subscribe Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Boost Modal */}
      <Dialog open={showBoostModal} onOpenChange={setShowBoostModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Purchase {selectedBoost?.name}</DialogTitle>
            <DialogDescription>
              Boost your listing for {selectedBoost?.duration_days} days
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-orange-200">
              <div className="flex justify-between items-center">
                <span>Price:</span>
                <span className="text-2xl font-bold text-orange-600">{currencySymbol}{selectedBoost?.price}</span>
              </div>
            </div>
            
            <div>
              <Label>Your Email</Label>
              <Input
                type="email"
                placeholder="seller@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            
            <div>
              <Label>Listing ID to Boost</Label>
              <Input
                placeholder="Enter your listing ID"
                value={listingId}
                onChange={(e) => setListingId(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">Find this in your listing details page</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBoostModal(false)}>Cancel</Button>
            <Button onClick={handleBuyBoost}>
              <Rocket className="w-4 h-4 mr-2" />
              Purchase Boost
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
